import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, ButtonBase, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, Snackbar, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RedoIcon from "@mui/icons-material/Redo";
import SearchIcon from "@mui/icons-material/Search";
import UndoIcon from "@mui/icons-material/Undo";
import { useProject } from "../../api/project";
import { useSaveVegetationTypes, useVegetationTypes } from "../../api/vegetationType";
import { TenantVegetationType } from "../../types/tenantVegetationType";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import ModelClassSelect, { McFilterSelect } from "./vegetationTypes/ModelClassSelect";
import VegTypeInspector from "./vegetationTypes/VegTypeInspector";
import VtTableRow, { RowChip, VT_COLUMNS } from "./vegetationTypes/VtTableRow";
import AddVegetationTypesDialog, { BlankType } from "./vegetationTypes/AddVegetationTypesDialog";
import { useVtDraft } from "./vegetationTypes/useVtDraft";
import {
  FIELD_LABEL, PALETTE, RowInfo, VtField, VtRow, buildBatch, diffRow, fmtField, loadUnit, matchesMcFilter, maxLoad, mcGroups,
  mcLabel, nameKey, num, plural, rowFromLibrary, rowFromServer, validateRow,
} from "./vegetationTypes/vtModel";
import {
  BLUE, BLUE_TEXT, FAINT, LINE, LINE_STRONG, MUTED, PANEL, PANEL_HEAD, RED_TEXT, TEAL, TEXT,
  btnGhost, btnPlain, btnPrimary, btnSmall, checkboxSx, chipSx, fieldSx, iconBtnSx, kbdSx, panelSx,
} from "./vegetationTypes/vtTheme";

interface VegetationTypesTabProps {
  onSaved?: () => void;
  /** Reports unsaved edits, so the modal can ask before they are lost. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Set by the modal when someone closes it, or switches tab, with unsaved edits. */
  leaveRequest?: "close" | "tab" | null;
  onLeaveCancel?: () => void;
  onLeaveConfirm?: () => void;
}

type SortKey = "name" | "mc" | "max" | "rate" | "t90";

/** Height the configuration modal leaves its tabs (90% of the screen less its title, tabs and footer). */
const TAB_HEIGHT = "calc(var(--vh100) * 0.9 - 230px)";

const chipFor = (i: RowInfo): RowChip | null => {
  if (i.r.deleted) return { label: "Deleted", tone: "del" };
  if (!i.s) return { label: "New", tone: "new" };
  if (i.hasErr) return { label: "Invalid", tone: "del" };
  if (i.changes.length) return { label: "Modified", tone: "mod" };
  return null;
};

const VegetationTypesTab: React.FC<VegetationTypesTabProps> = ({ onSaved, onDirtyChange, leaveRequest = null, onLeaveCancel, onLeaveConfirm }) => {
  const unit = useUnitSystem();
  const { data: vegetationTypesResp, isLoading, error } = useVegetationTypes();
  const { project } = useProject();
  const saveMutation = useSaveVegetationTypes();
  const classes = useMemo(() => (project?.data?.model_classes || []).map((mc: unknown) => String(mc)), [project?.data?.model_classes]);
  const groups = useMemo(() => mcGroups(classes), [classes]);

  const { saved, draft, canUndo, canRedo, reset, mutate, updateRow, undo, redo } = useVtDraft();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"all" | "modified">("all");
  const [mcFilter, setMcFilter] = useState("*");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const [compare, setCompare] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkMax, setBulkMax] = useState("");
  const [bulkRate, setBulkRate] = useState("");
  const [savedOnce, setSavedOnce] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; undo?: boolean } | null>(null);
  const nextId = useRef(1);

  // ---------------------------------------------------------------- derived
  const savedByKey = useMemo(() => new Map(saved.map((s) => [s.key, s])), [saved]);
  const info = useMemo<RowInfo[]>(() => {
    // Names must be unique, ignoring case as the add dialog does. Only a
    // name typed or added in this draft is flagged, so names that already
    // coexist never block a save.
    const nameCount = new Map<string, number>();
    draft.forEach((r) => { if (!r.deleted) nameCount.set(nameKey(r.name), (nameCount.get(nameKey(r.name)) ?? 0) + 1); });
    return draft.map((r) => {
      const s = savedByKey.get(r.key);
      const changes = diffRow(r, s);
      const fields: RowInfo["fields"] = {};
      changes.forEach((c) => { if (c.field !== "added" && c.field !== "deleted") fields[c.field] = true; });
      const nameEdited = !s || r.name.trim() !== s.name.trim();
      // A saved value is only checked once someone edits it.
      const errs = r.deleted ? {} : validateRow(
        r, unit, nameEdited && (nameCount.get(nameKey(r.name)) ?? 0) > 1, { max: !s || !!fields.max, rate: !s || !!fields.rate },
      );
      return { r, s, changes, errs, fields, hasErr: Object.keys(errs).length > 0 };
    });
  }, [draft, savedByKey, unit]);

  const changeCount = info.reduce((n, i) => n + i.changes.length, 0);
  const typesChanged = info.filter((i) => i.changes.length).length;
  const errorCount = info.filter((i) => i.hasErr).length;
  const dirty = changeCount > 0;
  const live = useMemo(() => draft.filter((r) => !r.deleted), [draft]);
  const projectNames = useMemo(() => new Set(live.map((r) => nameKey(r.name))), [live]);

  // Every sparkline shares one top so the curves compare across rows.
  const yMax = useMemo(() => {
    const cap = maxLoad(unit);
    let top = cap / 10;
    [...draft, ...saved].forEach((r) => { const v = num(r.max); if (Number.isFinite(v) && v <= cap && v > top) top = v; });
    const step = top <= 20 ? 5 : top <= 50 ? 10 : 20;
    return Math.ceil(top / step) * step;
  }, [draft, saved, unit]);

  const q = query.trim().toLowerCase();
  const filtered = !!q || view !== "all" || mcFilter !== "*";
  const visible = useMemo(() => {
    let vis = info.filter(({ r, changes }) => {
      if (q && !r.name.toLowerCase().includes(q) && !mcLabel(r.mc).toLowerCase().includes(q)) return false;
      if (view === "modified" && !changes.length) return false;
      return matchesMcFilter(r.mc, mcFilter);
    });
    if (sort) {
      const val = ({ r }: RowInfo): string | number => {
        if (sort.key === "name") return r.name.toLowerCase();
        if (sort.key === "mc") return mcLabel(r.mc).toLowerCase();
        if (sort.key === "max") return Number.isFinite(num(r.max)) ? num(r.max) : -1;
        if (sort.key === "rate") return Number.isFinite(num(r.rate)) ? num(r.rate) : -1;
        return num(r.rate) > 0 ? Math.log(10) / num(r.rate) : 1e9;
      };
      vis = [...vis].sort((a, b) => {
        const x = val(a);
        const y = val(b);
        return (typeof x === "string" ? x.localeCompare(String(y)) : x - Number(y)) * sort.dir;
      });
    }
    return vis;
  }, [info, q, view, mcFilter, sort]);

  const selInfo = info.find((i) => i.r.key === selectedKey) ?? visible[0] ?? null;
  const selKey = selInfo?.r.key ?? null;
  const others = useMemo(
    () => live
      .filter((o) => o.key !== selKey)
      .map((o) => ({ key: `o:${o.key}`, max: num(o.max), rate: num(o.rate) }))
      .filter((o) => Number.isFinite(o.max) && Number.isFinite(o.rate) && o.max >= 0 && o.max <= maxLoad(unit) && o.rate >= 0 && o.rate <= 1),
    [live, selKey, unit],
  );

  const visibleActive = visible.filter((i) => !i.r.deleted);
  const checkedKeys = Object.keys(checked).filter((k) => checked[k]);
  const allChecked = visibleActive.length > 0 && visibleActive.every((i) => checked[i.r.key]);

  // ------------------------------------------------------- server and modal
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const serverRows = useMemo(() => vegetationTypesResp?.data?.map((vt) => rowFromServer(vt, unit)) ?? null, [vegetationTypesResp, unit]);
  useEffect(() => {
    // A refetch never overwrites edits in progress.
    if (serverRows && !dirtyRef.current) reset(serverRows);
  }, [serverRows, reset]);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Row handlers read the latest state through a ref, so memoised rows
  // never act on a stale draft.
  const latest = useRef({ draft, saved });
  latest.current = { draft, saved };

  const showToast = useCallback((msg: string, undoable = false) => setToast({ msg, undo: undoable }), []);

  const toggleChecked = useCallback((key: string) => {
    setChecked((c) => { const n = { ...c }; if (n[key]) delete n[key]; else n[key] = true; return n; });
  }, []);

  const patchRow = useCallback((key: string, patch: Partial<VtRow>, coalesce?: string) => updateRow(key, patch, coalesce), [updateRow]);

  const duplicate = useCallback((keys: string[]) => {
    const set = new Set(keys);
    const out: VtRow[] = [];
    let first: string | null = null;
    latest.current.draft.forEach((r) => {
      out.push(r);
      if (set.has(r.key) && !r.deleted) {
        const key = `new-${nextId.current++}`;
        first = first ?? key;
        out.push({ ...r, key, id: null, name: `${r.name.trim()} (copy)`, deleted: false });
      }
    });
    const count = out.length - latest.current.draft.length;
    if (!count) return;
    mutate(() => out);
    setSelectedKey(first);
    setChecked({});
    showToast(`Duplicated ${plural(count, "vegetation type")}`, true);
  }, [mutate, showToast]);

  const remove = useCallback((keys: string[]) => {
    const set = new Set(keys);
    // A type added in this draft simply goes; a saved one waits for Save.
    mutate((d) => d.filter((r) => !(set.has(r.key) && r.id === null)).map((r) => (set.has(r.key) ? { ...r, deleted: true } : r)));
    setChecked({});
    showToast(keys.length === 1 ? "Marked for deletion" : `Marked ${keys.length} types for deletion`, true);
  }, [mutate, showToast]);

  const restore = useCallback((key: string) => updateRow(key, { deleted: false }), [updateRow]);

  const revertField = (key: string, field: VtField | "added" | "deleted") => {
    const s = latest.current.saved.find((r) => r.key === key);
    if (field === "added") { mutate((d) => d.filter((r) => r.key !== key)); return; }
    if (!s) return;
    if (field === "deleted") { updateRow(key, { deleted: false }); return; }
    updateRow(key, { [field]: s[field] });
  };

  const revertRow = (key: string) => {
    const s = latest.current.saved.find((r) => r.key === key);
    if (!s) { revertField(key, "added"); return; }
    mutate((d) => d.map((r) => (r.key === key ? { ...s } : r)));
  };

  const insertRows = (rows: VtRow[], msg: string) => {
    if (!rows.length) return;
    mutate((d) => [...d, ...rows]);
    setSelectedKey(rows[0].key);
    setView("all"); setQuery(""); setMcFilter("*"); setSort(null); setChecked({}); setAddOpen(false);
    showToast(msg, true);
  };

  const newKeyAndColour = () => {
    const n = nextId.current++;
    return { key: `new-${n}`, color: PALETTE[n % PALETTE.length] };
  };

  const addFromLibrary = (items: TenantVegetationType[]) => {
    const rows = items.map((e) => { const { key, color } = newKeyAndColour(); return rowFromLibrary(e, key, color, unit); });
    insertRows(rows, `Added ${plural(rows.length, "type")} from your library`);
  };

  const addBlank = (b: BlankType) => {
    const { key, color } = newKeyAndColour();
    insertRows([{ key, id: null, name: b.name.trim(), mc: b.mc, max: String(num(b.max)), maxT: b.maxT, rate: String(num(b.rate)), color }], `Added ${b.name.trim()}`);
  };

  const applyBulk = (patch: Partial<VtRow>, what: string) => {
    const set = new Set(checkedKeys);
    const n = draft.filter((r) => set.has(r.key) && !r.deleted).length;
    if (!n) return;
    mutate((d) => d.map((r) => (set.has(r.key) && !r.deleted ? { ...r, ...patch } : r)));
    showToast(`Set ${what} on ${plural(n, "type")}`, true);
  };

  const save = async (after?: () => void) => {
    if (!dirty || errorCount || saveMutation.isPending) return;
    const msg = `Saved ${plural(changeCount, "change")} to ${plural(typesChanged, "vegetation type")}`;
    const selectedName = selInfo ? nameKey(selInfo.r.name) : null;
    setSaveError(null);
    try {
      const resp = await saveMutation.mutateAsync(buildBatch(draft, saved, unit));
      const rows = (resp.data ?? []).map((vt) => rowFromServer(vt, unit));
      reset(rows);
      setSelectedKey(rows.find((r) => nameKey(r.name) === selectedName)?.key ?? null);
      setReviewOpen(false);
      setChecked({});
      setSavedOnce(true);
      onSaved?.();
      if (after) after();
      else showToast(msg);
    } catch (e) {
      const err = e as { message?: string; data?: { fields?: Record<string, string> } };
      setSaveError(err?.message && err.message !== "Request failed" ? err.message : "Your changes could not be saved. Try again.");
      const failed = Object.keys(err?.data?.fields ?? {})[0];
      if (failed && draft.some((r) => r.key === failed)) setSelectedKey(failed);
    }
  };

  const discard = (after?: () => void) => {
    const n = changeCount;
    mutate(() => saved.map((r) => ({ ...r })));
    setReviewOpen(false);
    setSaveError(null);
    if (after) after();
    else showToast(`Discarded ${plural(n, "change")}`, true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (addOpen || leaveRequest) return;
    const mod = e.metaKey || e.ctrlKey;
    const k = e.key.toLowerCase();
    if (mod && k === "s") { e.preventDefault(); save(); }
    else if (mod && k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
    else if (mod && k === "y") { e.preventDefault(); redo(); }
  };

  const setSortKey = (key: SortKey) => setSort((s) => (!s || s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null));

  // ------------------------------------------------------------------ view
  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress sx={{ color: TEAL }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ color: RED_TEXT }}>Failed to load vegetation types</Typography>;
  }

  const review = info.filter((i) => i.changes.length);
  const bm = num(bulkMax);
  const br = num(bulkRate);
  const unitLabel = loadUnit(unit);
  const sortArrow = (key: SortKey) => (sort?.key === key ? (sort.dir === 1 ? "↑" : "↓") : "");
  const sortHead = (key: SortKey, label: string, numeric = false) => (
    <ButtonBase
      onClick={() => setSortKey(key)}
      sx={{
        width: "100%", height: 42, px: 1.25, gap: 0.5, justifyContent: numeric ? "flex-end" : "flex-start", fontSize: 11, fontWeight: 600,
        letterSpacing: "0.05em", textTransform: "uppercase", color: MUTED, whiteSpace: "nowrap", "&:hover": { color: TEXT },
      }}
    >
      {label} <Box component="span" sx={{ color: TEAL, fontSize: 12 }}>{sortArrow(key)}</Box>
    </ButtonBase>
  );
  const segBtn = (on: boolean) => ({
    height: 28, px: 1.25, borderRadius: 1.5, fontSize: 13, fontWeight: 500, textTransform: "none" as const, minWidth: 0, gap: 0.75,
    color: on ? TEXT : MUTED, bgcolor: on ? "rgba(255,255,255,0.1)" : "transparent", "&:hover": { bgcolor: on ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)", color: TEXT },
  });
  const divider = <Box sx={{ width: "1px", height: 22, bgcolor: LINE_STRONG }} />;
  const leaveTitle = leaveRequest === "tab" ? "Save changes before leaving this tab?" : "Save changes before closing?";

  return (
    <Box onKeyDown={onKeyDown} sx={{ color: TEXT, display: "flex", flexDirection: "column", minHeight: TAB_HEIGHT }}>
      <Box>
        <Typography component="h2" sx={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.015em" }}>Vegetation types</Typography>
        <Typography sx={{ mt: 0.75, fontSize: 13, lineHeight: 1.5, color: MUTED, maxWidth: 680 }}>
          Fuel parameters for each vegetation class in this project. Edit them in the table or the side panel; every change stays a draft until you save them together.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 3, mt: 2.5 }}>
        <Box component="section" aria-label="Vegetation type table" sx={{ flex: "999 1 640px", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, mb: 1.5, minHeight: 44 }}>
            {checkedKeys.length === 0 ? (
              <>
                <TextField
                  size="small"
                  type="search"
                  placeholder="Search name or model class"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{ flex: "1 1 220px", maxWidth: 320 }}
                  slotProps={{
                    input: { sx: fieldSx(), startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: FAINT }} /></InputAdornment> },
                    htmlInput: { "aria-label": "Search vegetation types" },
                  }}
                />
                <Box role="group" aria-label="Show" sx={{ display: "inline-flex", gap: 0.25, p: "3px", borderRadius: 2.25, bgcolor: "rgba(0,0,0,0.22)", border: `1px solid ${LINE}` }}>
                  {([["all", "All", draft.length], ["modified", "Modified", typesChanged]] as const).map(([v, label, n]) => (
                    <Button key={v} sx={segBtn(view === v)} onClick={() => setView(v)} aria-pressed={view === v}>
                      {label} <Box component="span" sx={{ ...chipSx("plain"), height: 18, px: 0.75, fontSize: 11 }}>{n}</Box>
                    </Button>
                  ))}
                </Box>
                <McFilterSelect value={mcFilter} classes={classes} onChange={setMcFilter} ariaLabel="Filter by model class" />
                {filtered && <Button sx={{ ...btnGhost, ...btnSmall }} onClick={() => { setQuery(""); setView("all"); setMcFilter("*"); }}>Clear filters</Button>}
                <Box sx={{ flex: 1 }} />
                {!dirty && (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13, color: MUTED, whiteSpace: "nowrap" }}>
                    <CheckIcon sx={{ fontSize: 17, color: TEAL }} />{savedOnce ? "All changes saved" : "No unsaved changes"}
                  </Box>
                )}
                <Button sx={{ ...btnPrimary, height: 36, gap: 0.75 }} startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add vegetation type</Button>
              </>
            ) : (
              <Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, p: "5px 6px 5px 14px", borderRadius: 2.5, bgcolor: "rgba(0,212,173,0.07)", border: "1px solid rgba(0,212,173,0.32)" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mr: 0.75 }}>{checkedKeys.length} selected</Typography>
                {divider}
                <Box sx={{ width: 220 }}>
                  <ModelClassSelect variant="field" value="" groups={groups} placeholder="Set model class…" height={32} ariaLabel="Set model class for selected" onChange={(mc) => applyBulk({ mc }, "model class")} />
                </Box>
                {([
                  ["bulk-max", `Max ${unitLabel}`, `Max fuel load for selected, ${unitLabel}`, bulkMax, setBulkMax, !(Number.isFinite(bm) && bm >= 0 && bm <= maxLoad(unit)), () => { applyBulk({ max: String(bm) }, "max fuel load"); setBulkMax(""); }, 112],
                  ["bulk-rate", "Rate k", "Accumulation rate for selected", bulkRate, setBulkRate, !(Number.isFinite(br) && br >= 0 && br <= 1), () => { applyBulk({ rate: String(br) }, "accumulation rate"); setBulkRate(""); }, 96],
                ] as const).map(([id, placeholder, label, value, set, off, apply, width]) => (
                  <Box key={id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TextField
                      id={id}
                      size="small"
                      placeholder={placeholder}
                      value={value}
                      onChange={(e) => set(e.target.value)}
                      sx={{ width }}
                      slotProps={{ input: { sx: { ...fieldSx(), height: 32 } }, htmlInput: { inputMode: "decimal", "aria-label": label } }}
                    />
                    <Button sx={{ ...btnPlain, ...btnSmall, height: 30 }} disabled={off} onClick={apply}>Apply</Button>
                  </Box>
                ))}
                {divider}
                <Button sx={{ ...btnGhost, ...btnSmall, height: 30, gap: 0.5 }} startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: "15px !important" }} />} onClick={() => duplicate(checkedKeys)}>Duplicate</Button>
                <Button sx={{ ...btnGhost, ...btnSmall, height: 30, gap: 0.5, color: RED_TEXT }} startIcon={<DeleteOutlineIcon sx={{ fontSize: "16px !important" }} />} onClick={() => remove(checkedKeys)}>Delete</Button>
                <Box sx={{ flex: 1 }} />
                <IconButton sx={iconBtnSx} aria-label="Clear selection" onClick={() => setChecked({})}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ ...panelSx, overflowX: "auto" }}>
            <Box sx={{ minWidth: 920 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: VT_COLUMNS, alignItems: "center", height: 42, borderBottom: `1px solid ${LINE}`, bgcolor: PANEL_HEAD }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Checkbox
                    size="small"
                    sx={checkboxSx}
                    checked={allChecked}
                    indeterminate={!allChecked && checkedKeys.length > 0}
                    onChange={() => {
                      if (allChecked) setChecked({});
                      else setChecked(Object.fromEntries(visibleActive.map((i) => [i.r.key, true])));
                    }}
                    inputProps={{ "aria-label": "Select all shown" }}
                  />
                </Box>
                {sortHead("name", "Vegetation class")}
                {sortHead("mc", "Model class")}
                {sortHead("max", `Max ${unitLabel}`, true)}
                {sortHead("rate", "Rate k", true)}
                {sortHead("t90", "90% at", true)}
                <Box sx={{ px: 1.25, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: MUTED, textAlign: "center" }}>Curve</Box>
                <Box />
              </Box>

              {visible.map((i) => (
                <VtTableRow
                  key={i.r.key}
                  info={i}
                  chip={chipFor(i)}
                  selected={i.r.key === selKey}
                  checked={!!checked[i.r.key]}
                  groups={groups}
                  unit={unit}
                  yMax={yMax}
                  onSelect={setSelectedKey}
                  onCheck={toggleChecked}
                  onPatch={patchRow}
                  onDuplicate={(key) => duplicate([key])}
                  onDelete={(key) => remove([key])}
                  onRestore={restore}
                />
              ))}

              {visible.length === 0 && (
                <Box sx={{ p: "48px 24px", textAlign: "center" }}>
                  {draft.length === 0 ? (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No vegetation types yet</Typography>
                      <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>Add them from your library, or start from a blank type.</Typography>
                      <Button sx={{ ...btnPlain, ...btnSmall, mt: 1.75 }} onClick={() => setAddOpen(true)}>Add vegetation type</Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No vegetation types match</Typography>
                      <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>Try a different search or filter.</Typography>
                      <Button sx={{ ...btnPlain, ...btnSmall, mt: 1.75 }} onClick={() => { setQuery(""); setView("all"); setMcFilter("*"); }}>Clear filters</Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <VegTypeInspector
          info={selInfo}
          chip={selInfo ? chipFor(selInfo) : null}
          groups={groups}
          unit={unit}
          others={others}
          compare={compare}
          onCompare={setCompare}
          onPatch={(patch, coalesce) => { if (selKey) updateRow(selKey, patch, coalesce); }}
          onRevertField={(field) => { if (selKey) revertField(selKey, field); }}
          onRevertAll={() => { if (selKey) revertRow(selKey); }}
          onDuplicate={() => { if (selKey) duplicate([selKey]); }}
          onDelete={() => { if (selKey) remove([selKey]); }}
          onRestore={() => { if (selKey) restore(selKey); }}
          maxHeight={dirty ? `calc(${TAB_HEIGHT} - 72px)` : TAB_HEIGHT}
        />
      </Box>

      {dirty && (
        <Box sx={{ position: "sticky", bottom: 0, zIndex: 3, mt: "auto", mx: -3, mb: -3, pt: 0, bgcolor: "rgba(26,29,46,0.95)", backdropFilter: "blur(10px)", borderTop: `1px solid ${LINE_STRONG}`, boxShadow: "0 -12px 32px rgba(0,0,0,0.35)" }}>
          {reviewOpen && (
            <Box sx={{ borderBottom: `1px solid ${LINE}`, maxHeight: 320, overflow: "auto" }}>
              <Box sx={{ px: 3, py: 2, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 1.5 }}>
                {review.map((g) => (
                  <Box key={g.r.key} sx={{ bgcolor: PANEL, border: `1px solid ${LINE}`, borderRadius: 2.5, p: "12px 12px 8px 14px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      <Box sx={{ width: 14, height: 14, borderRadius: 1, flex: "none", bgcolor: g.r.color, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.2)" }} />
                      <ButtonBase
                        onClick={() => setSelectedKey(g.r.key)}
                        title={g.r.name}
                        sx={{ flex: 1, minWidth: 0, justifyContent: "flex-start", fontSize: 13, fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", textAlign: "left" }}
                      >
                        {g.r.name.trim() || "Untitled"}
                      </ButtonBase>
                    </Box>
                    {g.changes.map((c) => {
                      const from = c.field === "added" ? "—" : c.field === "deleted" ? "Active" : fmtField(c.field, c.from ?? "", unit);
                      const to = c.field === "added" ? "New" : c.field === "deleted" ? "Deleted" : fmtField(c.field, c.to ?? "", unit);
                      return (
                        <Box key={c.field} sx={{ display: "flex", alignItems: "center", gap: 1.25, fontSize: 12, minHeight: 30 }}>
                          <Box sx={{ color: MUTED, width: 112, flex: "none" }}>{FIELD_LABEL[c.field]}</Box>
                          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${from} → ${to}`}>
                            <Box component="span" sx={{ color: MUTED }}>{from}</Box> → <Box component="strong" sx={{ fontWeight: 600, color: BLUE_TEXT }}>{to}</Box>
                          </Box>
                          <Button sx={{ ...btnGhost, ...btnSmall }} onClick={() => revertField(g.r.key, c.field)}>Revert</Button>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ px: 3, py: 1.5, display: "flex", flexWrap: "wrap", gap: "10px 14px", alignItems: "center" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BLUE, boxShadow: "0 0 0 4px rgba(90,169,236,0.18)", flex: "none" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{plural(changeCount, "unsaved change")}</Typography>
            <Typography sx={{ fontSize: 13, color: MUTED }}>across {plural(typesChanged, "vegetation type")}</Typography>
            {errorCount > 0 && <Box sx={chipSx("del")}>Fix {plural(errorCount, "invalid type")} to save</Box>}
            {saveError && <Box sx={{ ...chipSx("del"), height: "auto", minHeight: 22, py: 0.25, whiteSpace: "normal" }}>{saveError}</Box>}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Undo (⌘Z)"><span><IconButton sx={iconBtnSx} aria-label="Undo" disabled={!canUndo} onClick={undo}><UndoIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
            <Tooltip title="Redo (⇧⌘Z)"><span><IconButton sx={iconBtnSx} aria-label="Redo" disabled={!canRedo} onClick={redo}><RedoIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
            <Button sx={{ ...btnGhost, height: 36 }} aria-expanded={reviewOpen} onClick={() => setReviewOpen((o) => !o)}>{reviewOpen ? "Hide changes" : "Review changes"}</Button>
            <Button sx={{ ...btnPlain, height: 36 }} disabled={saveMutation.isPending} onClick={() => discard()}>Discard</Button>
            <Button sx={{ ...btnPrimary, height: 36, gap: 1 }} disabled={saveMutation.isPending || errorCount > 0} onClick={() => save()}>
              {saveMutation.isPending ? "Saving…" : changeCount > 1 ? "Save all changes" : "Save change"}
              <Box component="span" sx={{ ...kbdSx, color: "inherit", bgcolor: "rgba(6,48,42,0.16)", borderColor: "transparent" }}>⌘S</Box>
            </Button>
          </Box>
        </Box>
      )}

      <AddVegetationTypesDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        classes={classes}
        groups={groups}
        unit={unit}
        projectNames={projectNames}
        onAddLibrary={addFromLibrary}
        onAddBlank={addBlank}
      />

      <Dialog open={!!leaveRequest} onClose={onLeaveCancel} PaperProps={{ sx: { bgcolor: PANEL_HEAD, color: TEXT, width: 440, border: `1px solid ${LINE_STRONG}`, borderRadius: 3.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>{leaveTitle}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: MUTED }}>
            You have {plural(changeCount, "unsaved change")} across {plural(typesChanged, "vegetation type")}.
            {errorCount > 0 ? " Fix the invalid values before saving, or discard them." : ""}
          </Typography>
          {saveError && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12.5 }}>{saveError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button sx={btnGhost} onClick={onLeaveCancel}>Keep editing</Button>
          <Button sx={btnPlain} disabled={saveMutation.isPending} onClick={() => discard(onLeaveConfirm)}>Discard</Button>
          <Button sx={btnPrimary} disabled={saveMutation.isPending || errorCount > 0} onClick={() => save(onLeaveConfirm)}>
            {saveMutation.isPending ? "Saving…" : leaveRequest === "tab" ? "Save & continue" : "Save & close"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={(_, reason) => { if (reason !== "clickaway") setToast(null); }} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: 1.75, pr: 1, py: 1, borderRadius: 2.25, bgcolor: "#e9eef3", color: "#141a21", fontWeight: 500, fontSize: 13, boxShadow: "0 10px 30px rgba(0,0,0,0.4)", whiteSpace: "nowrap" }}>
          <CheckIcon sx={{ fontSize: 16, color: "#0a8f76" }} />
          <span>{toast?.msg}</span>
          {toast?.undo && (
            <Button sx={{ ...btnSmall, textTransform: "none", bgcolor: "#141a21", color: "#fff", "&:hover": { bgcolor: "#2a3642" } }} onClick={() => { undo(); setToast(null); }}>Undo</Button>
          )}
          <IconButton size="small" aria-label="Dismiss" onClick={() => setToast(null)} sx={{ color: "#141a21" }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      </Snackbar>
    </Box>
  );
};

export default VegetationTypesTab;
